import { FC, Fragment, ReactNode, useEffect } from 'react';
import {
  Box,
  List,
  Pagination,
} from '@mui/material';

import { usePagination } from '@cvt/hooks/usePagination';
import { MessageFeedbackView } from '@cvt/components/MessageFeedbackView';

import { useMajors } from '../hooks/useMajors';

const displayAmount = 10;

interface ViewProps {
  filters?: Majors.GetListParams;
  renderItem: (major: Majors.Major) => ReactNode;
  options?: {
    enablePagination?: boolean;
  };
  slots?: {
    after?: ReactNode | ((props: { count: number, majors: Majors.Major[] }) => ReactNode);
  };
}

export const MajorsList: FC<ViewProps> = ({ filters = {}, renderItem, options, slots }) => {

  const { totalPages, setCount, page, setPage, limit, offset } = usePagination(filters.limit || displayAmount);

  const { count, majors, status } = useMajors({
    ...filters,
    offset,
    limit,
  });

  useEffect(() => {
    if (status === 'success') {
      setCount(count);
    }
  }, [status, setCount, count]);

  const renderAfterSlot = () => {
    if (!slots?.after) return null;
    
    if (typeof slots.after === 'function') {
      return slots.after({
        count,
        majors,
      });
    }
    
    return slots.after;
  };

  return (
    <Fragment>
      {status === 'error' && (
        <MessageFeedbackView height="100%"/>
      )}
      {status === 'success' && (
        <Fragment>
          {majors.length === 0 && (
            <MessageFeedbackView height={400} message="No Majors"/>
          )}
          {majors.length > 0 && (
            <List disablePadding>
              {majors.map(major => renderItem(major))}
            </List>
          )}
          {renderAfterSlot()}
          {options?.enablePagination && majors.length > 0 && (
            <Box display="flex" width="100%" p={2} mt="auto" justifyContent="center">
              <Pagination
                count={totalPages}
                page={page}
                onChange={(event, value) => setPage(value)}
                showFirstButton={page !== 1}
                showLastButton={page !== totalPages}
              />
            </Box>
          )}
        </Fragment>
      )}
    </Fragment>
  );
};
